import React, { useState, useEffect } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { WarningCircleIcon, CheckIcon } from '@phosphor-icons/react';

interface JSONEditorProps {
  data: unknown;
  onSave: (data: unknown) => Promise<void>;
  onCancel?: () => void;
  title?: string;
  disabled?: boolean;
  height?: string;
}

export function JSONEditor({
  data,
  onSave,
  onCancel,
  title = 'JSON Data',
  disabled = false,
  height = 'h-64',
}: JSONEditorProps) {
  const gt = useGT();
  const [isEditing, setIsEditing] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize JSON value when data changes or editing starts
  useEffect(() => {
    if (isEditing) {
      setJsonValue(data ? JSON.stringify(data, null, 2) : '{}');
    }
  }, [data, isEditing]);

  // Validate JSON and update error state
  const validateJSON = (value: string) => {
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      return false;
    }
  };

  const handleJSONChange = (value: string) => {
    setJsonValue(value);
    validateJSON(value);
  };

  const handleSave = async () => {
    if (!validateJSON(jsonValue)) {
      return;
    }

    setIsSaving(true);
    try {
      const parsedData = JSON.parse(jsonValue);
      await onSave(parsedData);
      setIsEditing(false);
      setJsonError(null);
    } catch (error) {
      console.error('Error saving JSON:', error);
      setJsonError('Failed to save data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setJsonError(null);
    setJsonValue('');
    onCancel?.();
  };

  const canSave = !jsonError && jsonValue.trim() !== '' && !isSaving;

  if (!isEditing) {
    // Display mode
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">{title}</h3>
          <Button
            onClick={() => setIsEditing(true)}
            disabled={disabled}
            variant="default"
          >
            <T>Edit</T>
          </Button>
        </div>
        <div className="bg-gray-50 rounded-md p-4 border">
          <pre className="text-sm font-mono whitespace-pre-wrap overflow-auto">
            {data
              ? JSON.stringify(data, null, 2)
              : gt('No data available', {
                  context: 'placeholder when JSON editor has no data',
                })}
          </pre>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">{title}</h3>
        <Button onClick={handleCancel} variant="outline" disabled={isSaving}>
          <T>Cancel</T>
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="json-editor" className="text-sm font-medium">
          <T>JSON Content</T>
        </Label>
        <div className="relative">
          <textarea
            id="json-editor"
            value={jsonValue}
            onChange={(e) => handleJSONChange(e.target.value)}
            className={`w-full ${height} p-3 border rounded-md font-mono text-sm resize-none ${
              jsonError
                ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
            }`}
            placeholder="Enter valid JSON..."
            disabled={isSaving}
          />

          {/* JSON Status Indicator */}
          <div className="absolute top-3 right-3">
            {jsonError ? (
              <WarningCircleIcon className="h-5 w-5 text-red-500" />
            ) : jsonValue.trim() !== '' ? (
              <CheckIcon className="h-5 w-5 text-green-500" />
            ) : null}
          </div>
        </div>

        {/* Error Message */}
        {jsonError && (
          <div className="flex items-start space-x-2 text-red-600 text-sm">
            <WarningCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">
                <T>Invalid JSON:</T>
              </p>
              <p>{jsonError}</p>
            </div>
          </div>
        )}

        {/* Success indicator for valid JSON */}
        {!jsonError && jsonValue.trim() !== '' && (
          <div className="flex items-center space-x-2 text-green-600 text-sm">
            <CheckIcon className="h-4 w-4" />
            <p>
              <T>Valid JSON format</T>
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          className="min-w-[80px]"
        >
          {isSaving ? <T>Saving...</T> : <T>Save</T>}
        </Button>
        <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
          <T>Cancel</T>
        </Button>
      </div>
    </div>
  );
}

export default JSONEditor;
